/**
 * Configuration i18n — stratégie "as-needed".
 *
 * - FR (locale par défaut) : URLs d'origine, SANS préfixe (/chauffeur, /location…).
 *   Aucune URL FR ne change → index et SEO FR intacts.
 * - EN : versions anglaises sous le préfixe /en (/en/chauffeur/partners…).
 *
 * Règle SEO : PAS de redirection automatique selon la langue du NAVIGATEUR
 * (Accept-Language — Google le déconseille). On s'appuie sur hreflang (Google
 * sert la bonne version) + un switcher manuel. Voir buildLanguageAlternates().
 *
 * En revanche, le CHOIX EXPLICITE de l'utilisateur (clic switcher) est mémorisé
 * (localStorage + cookie) et restitué à sa prochaine visite par une redirection
 * CÔTÉ CLIENT — voir `components/i18n/LocalePreference`. Gated sur localStorage :
 * un bot (sans préférence) n'est jamais redirigé → SEO-safe, à ne pas confondre
 * avec la détection navigateur ci-dessus.
 *
 * Consommé par : metadataRouter, jsonLdRouter, sitemap, LanguageSwitcher,
 * pages /en/*.
 */

export const LOCALES = ["fr", "en", "ar"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "fr";

/**
 * Locales pleinement déployées (toutes pages traduites) → toujours proposées
 * dans le switcher. L'arabe est déployé INCRÉMENTALEMENT : il n'apparaît que
 * pour les pages qui ont réellement une version `/ar` (voir localeHasPage()
 * dans localeRoutes), pour ne jamais tomber sur un 404 pendant le déploiement.
 */
export const FULLY_ROLLED_OUT_LOCALES: readonly Locale[] = ["fr", "en"];

/** Préfixe d'URL par locale (FR = vide en as-needed). */
export const LOCALE_PREFIX: Record<Locale, string> = {
  fr: "",
  en: "/en",
  ar: "/ar",
};

/**
 * Code hreflang officiel par locale — **langue seule, sans région**.
 *
 * `fr` et non `fr-FR` : la variante régionale restreint le ciblage à la France
 * et dessert moins bien les francophones de Monaco, Belgique et Suisse, qui
 * pèsent dans les impressions. C'est aussi la valeur déjà émise par
 * `scripts/generate-sitemap.mjs` (`hreflang="fr"`) — les deux déclarations
 * doivent concorder pour un même cluster d'URLs.
 */
export const HREFLANG: Record<Locale, string> = {
  fr: "fr",
  en: "en",
  ar: "ar",
};

/** Sens d'écriture par locale (arabe = droite-à-gauche). */
export const LOCALE_DIR: Record<Locale, "ltr" | "rtl"> = {
  fr: "ltr",
  en: "ltr",
  ar: "rtl",
};

/** Sens d'écriture d'une locale (pour <html dir> et le CSS logique). */
export const dirFromLocale = (locale: Locale): "ltr" | "rtl" =>
  LOCALE_DIR[locale];

/** Détecte la locale d'un chemin (préfixe /en → en, /ar → ar, sinon fr). */
export const localeFromPath = (pathname: string): Locale => {
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  if (pathname === "/ar" || pathname.startsWith("/ar/")) return "ar";
  return "fr";
};

/** Retire le préfixe de locale d'un chemin (/en/chauffeur → /chauffeur). */
export const stripLocale = (pathname: string): string => {
  if (pathname === "/en" || pathname === "/ar") return "/";
  if (pathname.startsWith("/en/") || pathname.startsWith("/ar/")) {
    return pathname.slice(3) || "/";
  }
  return pathname;
};

/**
 * Construit le bloc `alternates.languages` (hreflang) pour Next metadata,
 * à partir des canoniques FR/EN (et AR si la page existe) d'une même page.
 * x-default pointe sur la version FR (marché et contenu de référence).
 * `arCanonical` est OPTIONNEL : on n'émet le hreflang `ar` que pour les pages
 * réellement traduites (déploiement incrémental).
 */
export const buildLanguageAlternates = (
  frCanonical: string,
  enCanonical: string,
  arCanonical?: string,
): Record<string, string> => ({
  [HREFLANG.fr]: frCanonical,
  [HREFLANG.en]: enCanonical,
  ...(arCanonical ? { [HREFLANG.ar]: arCanonical } : {}),
  "x-default": frCanonical,
});
